import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Image, Upload, X, Search, Trash2, Check, Loader2, 
  ArrowLeft, Grid3x3, ImageIcon, Sparkles, Leaf, Edit2, AlertTriangle,
  ChevronLeft, ChevronRight
} from 'lucide-react';
import AdminHeader from '../components/AdminHeader';
import ImageEditor from '../components/ImageEditor';
import ConfirmModal from '../components/ConfirmModal';
import { imageGalleryAPI } from '../services/api';
import { compressImage } from '../utils/imageCompression';
import { useToast } from '../contexts/ToastContext';
import logger from '../utils/logger';
import './ImageGalleryPage.css';

function ImageGalleryPage(props) {
  const { onSelectImage, onSelectImages, isModal = false, onClose, allowMultiSelect = false } = props || {};
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({
    current: 0,
    total: 0,
    phase: 'compressing' // 'compressing' or 'uploading'
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedImages, setSelectedImages] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [deletingId, setDeletingId] = useState(null);
  const [editingImage, setEditingImage] = useState(null);
  const [multiSelectMode, setMultiSelectMode] = useState(true); // Always enable multi-select mode
  const [confirmModal, setConfirmModal] = useState(null);
  const { addToast } = useToast();

  // Determine if this instance should behave as a simple single-select modal
  const isExplicitSingleSelectModal = isModal && !allowMultiSelect && !!onSelectImage;

  // Get callback from location state (for route mode) or props
  const selectCallback = onSelectImage || location.state?.onSelectImage;
  // Only enable multi-select callback when allowed
  const selectMultipleCallback = allowMultiSelect
    ? (onSelectImages || location.state?.onSelectImages || (multiSelectMode ? () => {} : null))
    : null;

  useEffect(() => {
    fetchImages();
  }, [page, searchQuery]);

  const fetchImages = async () => {
    try {
      setLoading(true);
      const response = await imageGalleryAPI.getImages({
        page,
        limit: 30,
        search: searchQuery
      });
      
      if (response.success) {
        setImages(response.data || []);
        if (response.pagination) {
          setTotalPages(response.pagination.pages || 1);
        }
      }
    } catch (error) {
      logger.error('Error fetching images:', error);
      addToast({
        type: 'error',
        message: 'Failed to load images from gallery. Please refresh the page.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (files) => {
    const fileArray = Array.from(files).filter(file => file.type.startsWith('image/'));
    
    if (fileArray.length === 0) {
      addToast({
        type: 'warning',
        message: 'Please select valid image files'
      });
      return;
    }

    // Limit batch size to prevent timeout
    const MAX_BATCH_SIZE = 100;
    if (fileArray.length > MAX_BATCH_SIZE) {
      setConfirmModal({
        isOpen: true,
        title: 'Too Many Images Selected',
        message: `You selected ${fileArray.length} images. For best performance, please upload ${MAX_BATCH_SIZE} images at a time. Would you like to upload the first ${MAX_BATCH_SIZE} images now?`,
        confirmText: 'Upload First 100',
        cancelText: 'Cancel',
        type: 'warning',
        onConfirm: () => {
          setConfirmModal(null);
          proceedWithUpload(fileArray, MAX_BATCH_SIZE);
        },
        onClose: () => setConfirmModal(null)
      });
      return;
    }

    proceedWithUpload(fileArray, fileArray.length);
  };

  const proceedWithUpload = async (fileArray, batchSize) => {

    setUploading(true);
    const batch = fileArray.slice(0, batchSize);
    
    // Initialize progress
    setUploadProgress({
      current: 0,
      total: batchSize,
      phase: 'compressing'
    });
    
    try {
      // Process images with progress tracking
      const imageDataArray = [];
      
      for (let i = 0; i < batch.length; i++) {
        const file = batch[i];
        try {
          // Compress image
          const compressedBase64 = await compressImage(file, {
            maxWidth: 1920,
            maxHeight: 1920,
            quality: 0.85,
            maxSizeMB: 2
          });
          imageDataArray.push({
            image: compressedBase64,
            filename: file.name
          });
        } catch (error) {
          logger.error('Error compressing image:', error);
          // Fallback to base64 without compression
          const base64 = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => resolve(e.target.result);
            reader.readAsDataURL(file);
          });
          imageDataArray.push({
            image: base64,
            filename: file.name
          });
        }
        
        // Update compression progress
        setUploadProgress({
          current: i + 1,
          total: batchSize,
          phase: 'compressing'
        });
      }
      
      // Switch to uploading phase
      setUploadProgress({
        current: 0,
        total: batchSize,
        phase: 'uploading'
      });
      
      // Upload to gallery (duplicate checking happens on backend)
      const response = await imageGalleryAPI.uploadImages(imageDataArray);
      
      // Update progress to complete
      setUploadProgress({
        current: batchSize,
        total: batchSize,
        phase: 'uploading'
      });
      
      if (response.success) {
        // Refresh gallery
        await fetchImages();
        
        const successCount = response.data?.length || 0;
        const skippedCount = response.skipped || 0;
        const skippedDetails = response.skippedDetails || [];
        const errorCount = response.errors?.length || 0;
        
        // Build success message
        let message = '';
        if (successCount > 0) {
          message = `Successfully uploaded ${successCount} image(s)!`;
        }
        
        if (skippedCount > 0) {
          const duplicateFilenames = skippedDetails
            .filter(s => s.filename && s.filename !== 'unknown')
            .map(s => s.filename)
            .slice(0, 5); // Show first 5 filenames
          
          let skipMessage = `${skippedCount} duplicate(s) were skipped`;
          if (duplicateFilenames.length > 0) {
            skipMessage += ` (e.g., ${duplicateFilenames.join(', ')}${duplicateFilenames.length < skippedCount ? '...' : ''})`;
          }
          message += message ? `\n${skipMessage}.` : `${skipMessage}.`;
        }
        
        if (errorCount > 0) {
          message += message ? `\n${errorCount} image(s) failed to upload.` : `${errorCount} image(s) failed to upload.`;
        }
        
        if (successCount > 0) {
          addToast({
            type: 'success',
            message: `Successfully uploaded ${successCount} image(s)!`,
            duration: 4000
          });
        }
        
        if (skippedCount > 0) {
          const duplicateFilenames = skippedDetails
            .filter(s => s.filename && s.filename !== 'unknown')
            .map(s => s.filename)
            .slice(0, 3);
          
          let skipMessage = `${skippedCount} duplicate(s) were skipped`;
          if (duplicateFilenames.length > 0) {
            skipMessage += ` (${duplicateFilenames.join(', ')}${duplicateFilenames.length < skippedCount ? '...' : ''})`;
          }
          addToast({
            type: 'info',
            message: skipMessage,
            duration: 5000
          });
        }
        
        if (errorCount > 0) {
          addToast({
            type: 'error',
            message: `${errorCount} image(s) failed to upload. Please try again.`,
            duration: 5000
          });
        }
        
        if (fileArray.length > batchSize) {
          addToast({
            type: 'info',
            message: `Note: ${fileArray.length - batchSize} more image(s) were not uploaded. Please upload them in a separate batch.`,
            duration: 6000
          });
        }
      } else {
        throw new Error(response.message || 'Upload failed');
      }
    } catch (error) {
      logger.error('Error uploading images:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to upload images';
      addToast({
        type: 'error',
        message: `Upload failed: ${errorMessage}. Please try uploading fewer images at once or check your network connection.`,
        duration: 6000
      });
    } finally {
      // Reset progress after a short delay to show completion
      setTimeout(() => {
        setUploading(false);
        setUploadProgress({
          current: 0,
          total: 0,
          phase: 'compressing'
        });
      }, 500);
    }
  };

  const handleFileInputChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files);
    }
    // Reset input
    e.target.value = '';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelect(files);
    }
  };

  const handleDelete = async (imageId) => {
    const image = images.find(img => img._id === imageId);
    const imageName = image?.filename || image?.originalName || 'this image';
    
    setConfirmModal({
      isOpen: true,
      title: 'Delete Image',
      message: `Are you sure you want to delete "${imageName}" from the gallery? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'warning',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          setDeletingId(imageId);
          await imageGalleryAPI.deleteImage(imageId);
          await fetchImages();
          // Remove from selected if it was selected
          setSelectedImages(prev => prev.filter(img => img._id !== imageId));
          addToast({
            type: 'success',
            message: 'Image deleted successfully',
            duration: 3000
          });
        } catch (error) {
          logger.error('Error deleting image:', error);
          addToast({
            type: 'error',
            message: 'Failed to delete image. Please try again.',
            duration: 4000
          });
        } finally {
          setDeletingId(null);
        }
      },
      onClose: () => setConfirmModal(null)
    });
  };

  const handleDeleteMultiple = async () => {
    if (selectedImages.length === 0) return;

    const count = selectedImages.length;
    const imageNames = selectedImages
      .slice(0, 3)
      .map(img => img.filename || img.originalName || 'image')
      .join(', ');
    const moreText = count > 3 ? ` and ${count - 3} more` : '';
    
    setConfirmModal({
      isOpen: true,
      title: 'Delete Multiple Images',
      message: `Are you sure you want to delete ${count} image(s) from the gallery? This includes: ${imageNames}${moreText}. This action cannot be undone.`,
      confirmText: `Delete ${count} Images`,
      cancelText: 'Cancel',
      type: 'warning',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          // Delete all selected images
          const deletePromises = selectedImages.map(image => 
            imageGalleryAPI.deleteImage(image._id).catch(error => {
              logger.error(`Error deleting image ${image._id}:`, error);
              return { error: true, id: image._id };
            })
          );

          const results = await Promise.all(deletePromises);
          const errors = results.filter(r => r && r.error);
          const successCount = count - errors.length;

          // Clear selection
          setSelectedImages([]);

          // Refresh gallery
          await fetchImages();

          if (errors.length > 0) {
            addToast({
              type: 'warning',
              message: `Deleted ${successCount} image(s) successfully. ${errors.length} image(s) failed to delete.`,
              duration: 5000
            });
          } else {
            addToast({
              type: 'success',
              message: `Successfully deleted ${successCount} image(s)!`,
              duration: 4000
            });
          }
        } catch (error) {
          logger.error('Error deleting multiple images:', error);
          addToast({
            type: 'error',
            message: 'Failed to delete images. Please try again.',
            duration: 4000
          });
        }
      },
      onClose: () => setConfirmModal(null)
    });
  };

  const toggleImageSelection = (image) => {
    // Always allow multi-select
    setSelectedImages(prev => {
      const exists = prev.find(img => img._id === image._id);
      if (exists) {
        return prev.filter(img => img._id !== image._id);
      } else {
        return [...prev, image];
      }
    });
  };

  const handleConfirmSelection = () => {
    if (selectMultipleCallback && selectedImages.length > 0) {
      selectMultipleCallback(selectedImages.map(img => img.url));
      if (onClose) onClose();
      if (!isModal) navigate(-1);
    } else if (selectedImages.length > 0) {
      // No callback - just viewing (route mode)
      // Could show a preview or do nothing
    }
  };

  const handleEditImage = (image) => {
    setEditingImage(image);
  };

  const handleSaveEditedImage = async (editedImageUrl) => {
    if (!editingImage) return;

    try {
      // The edited image is already a base64 string from canvas
      // Compress it if needed by converting to image and back
      let processedImage = editedImageUrl;
      
      // Check if image needs compression (if it's too large)
      const img = new Image();
      img.onload = async () => {
        try {
          // Create canvas to compress
          const canvas = document.createElement('canvas');
          const maxWidth = 1920;
          const maxHeight = 1920;
          
          let width = img.width;
          let height = img.height;
          
          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width = width * ratio;
            height = height * ratio;
          }
          
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          processedImage = canvas.toDataURL('image/jpeg', 0.85);
          
          // Upload edited image to Cloudinary
          const response = await imageGalleryAPI.uploadImages([processedImage]);
          
          if (response.success && response.data && response.data.length > 0) {
            // Delete the old image entry
            await imageGalleryAPI.deleteImage(editingImage._id);
            
            // Refresh gallery to show updated image
            await fetchImages();
            addToast({
              type: 'success',
              message: 'Image edited and saved successfully!',
              duration: 4000
            });
          } else {
            throw new Error('Failed to save edited image');
          }
        } catch (error) {
          logger.error('Error saving edited image:', error);
          addToast({
            type: 'error',
            message: 'Failed to save edited image. Please try again.',
            duration: 4000
          });
        } finally {
          setEditingImage(null);
        }
      };
      
      img.onerror = () => {
        logger.error('Error loading edited image');
        addToast({
          type: 'error',
          message: 'Failed to process edited image. Please try again.',
          duration: 4000
        });
        setEditingImage(null);
      };
      
      img.src = editedImageUrl;
    } catch (error) {
      logger.error('Error saving edited image:', error);
      addToast({
        type: 'error',
        message: 'Failed to save edited image. Please try again.',
        duration: 4000
      });
      setEditingImage(null);
    }
  };

  const isImageSelected = (image) => {
    return selectedImages.some(img => img._id === image._id);
  };

  const handleSelectAll = () => {
    if (selectedImages.length === images.length) {
      // Deselect all
      setSelectedImages([]);
    } else {
      // Select all
      setSelectedImages([...images]);
    }
  };

  return (
    <div className={`image-gallery-page ${isModal ? 'modal-mode' : ''}`}>
      {!isModal && <AdminHeader />}
      
      <div className="image-gallery-container">
        {/* Header */}
        <div className="gallery-header">
          <div className="gallery-header-left">
            {!isModal && (
              <button 
                className="back-btn"
                onClick={() => navigate('/admin/products')}
              >
                <ArrowLeft size={20} />
                Back to Products
              </button>
            )}
            <h1>
              <ImageIcon size={28} />
              Image Gallery 🌿
            </h1>
            <p>Upload and manage reusable plant product photos</p>
          </div>
          
          {isModal && onClose && (
            <button className="close-modal-btn" onClick={onClose}>
              <X size={24} />
            </button>
          )}
        </div>

        {/* Upload Zone */}
        <div 
          className={`upload-zone ${dragActive ? 'drag-active' : ''} ${uploading ? 'uploading' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="upload-zone-content">
            <Leaf size={48} className="upload-icon" />
            <h3>Upload Plant Photos 🌱</h3>
            <p>Drag and drop images here, or click to browse</p>
            <button
              className="upload-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? (
                <>
                  <Loader2 size={18} className="spinning" />
                  {uploadProgress.phase === 'compressing' ? 'Compressing...' : 'Uploading...'}
                </>
              ) : (
                <>
                  <Upload size={18} />
                  Choose Files
                </>
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
            />
          </div>
          
          {/* Progress Bar */}
          {uploading && uploadProgress.total > 0 && (
            <div className="upload-progress-container">
              <div className="upload-progress-info">
                <span className="progress-phase">
                  {uploadProgress.phase === 'compressing' ? '🔄 Compressing images...' : '☁️ Uploading to Cloudinary...'}
                </span>
                <span className="progress-text">
                  {uploadProgress.current} / {uploadProgress.total} images
                </span>
              </div>
              <div className="upload-progress-bar">
                <div 
                  className="upload-progress-fill"
                  style={{ 
                    width: `${(uploadProgress.current / uploadProgress.total) * 100}%` 
                  }}
                />
              </div>
              <div className="progress-percentage">
                {Math.round((uploadProgress.current / uploadProgress.total) * 100)}%
              </div>
            </div>
          )}
        </div>

        {/* Search Bar */}
        <div className="gallery-search">
          <Search size={18} className="search-icon" />
          <input
            type="text"
            placeholder="Search images by name or tags..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
            className="search-input"
          />
        </div>

        {/* Selection Info & Mode Toggle */}
        <div className="gallery-controls-bar">
          {selectedImages.length > 0 && (
            <div className="selection-info">
              <div className="selection-left">
                <span className="selection-count">
                  <Check size={18} />
                  {selectedImages.length} of {images.length} image{selectedImages.length !== 1 ? 's' : ''} selected
                </span>
                {images.length > 0 && (
                  <button 
                    className="select-all-btn" 
                    onClick={handleSelectAll}
                    title={selectedImages.length === images.length ? 'Deselect All' : 'Select All'}
                  >
                    {selectedImages.length === images.length ? 'Deselect All' : 'Select All'}
                  </button>
                )}
              </div>
              <div className="selection-actions">
                <button 
                  className="delete-selected-btn" 
                  onClick={handleDeleteMultiple}
                  title="Delete Selected Images"
                >
                  <Trash2 size={18} />
                  Delete Selected ({selectedImages.length})
                </button>
                {selectMultipleCallback && (
                  <button className="confirm-selection-btn" onClick={handleConfirmSelection}>
                    <Check size={18} />
                    Use Selected Images ({selectedImages.length})
                  </button>
                )}
                <button 
                  className="clear-selection-btn" 
                  onClick={() => setSelectedImages([])}
                  title="Clear Selection"
                >
                  <X size={18} />
                  Clear
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Images Grid */}
        {loading ? (
          <div className="loading-state">
            <Loader2 size={32} className="spinning" />
            <p>Loading images...</p>
          </div>
        ) : images.length === 0 ? (
          <div className="empty-state">
            <ImageIcon size={64} />
            <h3>No images found</h3>
            <p>Upload your first plant photo to get started</p>
          </div>
        ) : (
          <>
            <div className="images-grid">
              {images.map((image) => (
                <div
                  key={image._id}
                  className={`image-card ${isImageSelected(image) ? 'selected' : ''} ${deletingId === image._id ? 'deleting' : ''}`}
                  onClick={() => {
                    // Only auto-select on click if in single-select modal mode
                    if (isExplicitSingleSelectModal && selectCallback) {
                      selectCallback(image.url);
                      if (onClose) onClose();
                      if (!isModal) navigate(-1);
                    }
                  }}
                >
                  <div className="image-card-overlay">
                    <button
                      className={`select-checkbox ${isImageSelected(image) ? 'selected' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        const wasSelected = isImageSelected(image);
                        toggleImageSelection(image);
                        if (!wasSelected && !isExplicitSingleSelectModal) {
                          // Show feedback when selecting (but not in single-select modal)
                          addToast({
                            type: 'success',
                            message: 'Image selected!',
                            duration: 2000
                          });
                        }
                      }}
                      title={isImageSelected(image) ? 'Deselect' : 'Select'}
                    >
                      {isImageSelected(image) ? <Check size={20} /> : <span className="checkbox-empty" />}
                    </button>
                    <div className="image-card-actions">
                      <button
                        className="edit-image-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditImage(image);
                        }}
                        title="Edit Image"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        className="delete-image-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(image._id);
                        }}
                        disabled={deletingId === image._id}
                        title="Delete Image"
                      >
                        {deletingId === image._id ? (
                          <Loader2 size={16} className="spinning" />
                        ) : (
                          <Trash2 size={16} />
                        )}
                      </button>
                    </div>
                  </div>
                  <img
                    src={image.url}
                    alt="Gallery item"
                    loading="lazy"
                  />
                  {image.size > 0 && (
                    <div className="image-card-info">
                      <span className="image-size">
                        {(image.size / 1024).toFixed(1)} KB
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="gallery-pagination">
                <div className="pagination-controls">
                  <button
                    type="button"
                    className="pagination-arrow-btn"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setPage(p => Math.max(1, p - 1));
                    }}
                    disabled={page === 1}
                    aria-label="Previous page"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  
                  <div className="pagination-numbers">
                    {(() => {
                      // Always show exactly 3 page buttons
                      let pagesToShow = [];
                      
                      if (totalPages <= 3) {
                        // If 3 or fewer pages, show all
                        pagesToShow = Array.from({ length: totalPages }, (_, i) => i + 1);
                      } else {
                        // Show 3 pages centered around current page
                        if (page === 1) {
                          // At start: show 1, 2, 3
                          pagesToShow = [1, 2, 3];
                        } else if (page === totalPages) {
                          // At end: show last 3 pages
                          pagesToShow = [totalPages - 2, totalPages - 1, totalPages];
                        } else {
                          // In middle: show current-1, current, current+1
                          pagesToShow = [page - 1, page, page + 1];
                        }
                      }
                      
                      return pagesToShow.map((pageNum) => (
                        <button
                          key={pageNum}
                          type="button"
                          className={`pagination-number ${page === pageNum ? 'active' : ''}`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setPage(pageNum);
                          }}
                        >
                          {pageNum}
                        </button>
                      ));
                    })()}
                  </div>
                  
                  <button
                    type="button"
                    className="pagination-arrow-btn"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setPage(p => Math.min(totalPages, p + 1));
                    }}
                    disabled={page === totalPages}
                    aria-label="Next page"
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Image Editor Modal */}
      {editingImage && (
        <ImageEditor
          imageUrl={editingImage.url}
          onSave={handleSaveEditedImage}
          onClose={() => setEditingImage(null)}
        />
      )}

      {/* Confirmation Modal */}
      {confirmModal && (
        <ConfirmModal
          isOpen={confirmModal.isOpen}
          onClose={confirmModal.onClose}
          onConfirm={confirmModal.onConfirm}
          title={confirmModal.title}
          message={confirmModal.message}
          confirmText={confirmModal.confirmText}
          cancelText={confirmModal.cancelText}
          type={confirmModal.type}
        />
      )}
    </div>
  );
}

export default ImageGalleryPage;

